import * as React from 'react';

import {api} from 'nutshell-core/api';
import {getPrimaryEmailAddress} from 'nutshell-core/utils/peep';

import {PandadocIcon, CloudDownloadIcon} from 'shells/icon';
import {Link} from 'shells/link/link';
import {SelectMenu} from 'shells/select-menu/select-menu';

import type {PeepSendToDestinations as PeepSendToDestinationsFragment} from 'nutshell-graphql-types';

import {
    shouldShowMailchimp,
    shouldShowConstantContact,
} from '../../integrations/should-show-buttons';

import styles from './peep-action-menu.module.css';

type Props = {
    anchor: HTMLElement | null | undefined;
    onClose: () => void;
    onConstantContactSelected: () => void;
    onQuickbooksSelected: () => void;
    isOpen: boolean;
    peep: PeepSendToDestinationsFragment;
};

export class PeepActionMenu extends React.PureComponent<Props> {
    // This div is needed to show the mailchimp popover.
    // Without it, any div that calls the popover unmounts
    // when the menu goes away, so we need an always rendered
    // div to mount.
    mailchimpPopoverDivRef: HTMLElement | null | undefined;

    render() {
        let optionKey = 1;
        const peep = this.props.peep;
        const menuOptions = [];

        if (!peep.isDeleted) {
            if (shouldShowMailchimp(NutClientConfig.integrations)) {
                menuOptions.push({
                    label: <div className={styles.button}>Send to Mailchimp</div>,
                    key: `${optionKey++}`,
                    handleSelect: this.handleSendToMailchimp,
                    icon: (
                        <img
                            src='/include/images/integration-logos/mailchimp-freddy.png'
                            className={styles.logo}
                        />
                    ),
                });
            }
            if (shouldShowConstantContact(NutClientConfig.integrations)) {
                menuOptions.push({
                    label: <div className={styles.button}>Send to Constant Contact</div>,
                    key: `${optionKey++}`,
                    handleSelect: this.props.onConstantContactSelected,
                    icon: (
                        <img
                            src='/include/images/integration-logos/constant-contact.png'
                            className={styles.logo}
                        />
                    ),
                });
            }
            menuOptions.push({
                label: <div className={styles.button}>Send to Quickbooks</div>,
                key: `${optionKey++}`,
                handleSelect: this.props.onQuickbooksSelected,
                icon: (
                    <img
                        src='/include/images/integration-logos/quickbooks.png'
                        className={styles.logo}
                    />
                ),
            });
        }

        menuOptions.push({
            label: (
                <Link href={`/send-to/pandadoc?id=${this.props.peep.id}`} newTab={true}>
                    Send to PandaDoc
                </Link>
            ),
            key: `${optionKey++}`,
            icon: <PandadocIcon size={24} />,
        });

        if (peep.__typename === 'Contact' && NutClientConfig.instanceOriginatedFromWebFX) {
            const emailAddress = getPrimaryEmailAddress(peep.emailAddresses);
            if (emailAddress) {
                const url = `https://app.webfx.com/marketingcloudfx/customer360/contacts?email[$like]=${emailAddress}&from=nutshell`;
                menuOptions.push({
                    label: (
                        <Link href={url} newTab={true}>
                            Open in RevenueCloudFX
                        </Link>
                    ),
                    key: `${optionKey++}`,
                    icon: <img src='/include/images/webfx/favicon.jpeg' className={styles.logo} />,
                });
            }
        }

        if (NutClientConfig.hasRightSignatureNUT6758) {
            menuOptions.push({
                label: (
                    // @ts-expect-error
                    <Link href={`/send-to/rightsignature?id=${this.props.peep.id}`} target='_blank'>
                        Send to RightSignature
                    </Link>
                ),
                key: `${optionKey++}`,
                icon: (
                    <img
                        src='/include/images/integration-logos/right-signature.JPEG'
                        className={styles.logo}
                    />
                ),
            });
        }

        menuOptions.push({
            label: <div className={styles.button}>Download as CSV</div>,
            variant: 'secondary',
            handleSelect: () => {
                const href = `/${peep.type}/list?selectedIds=${peep.id}&allData=true`;

                api.get(href, undefined, {
                    headers: {
                        Accept: 'text/csv; charset=utf-8',
                    },
                }).then(() => {
                    // This is a gearman job, and albeit a fast one,
                    // we don't get the download back right away, so we need
                    // to poll for it.
                    Nut.pollForNotifications();
                });
            },
            key: `${optionKey++}`,
            icon: <CloudDownloadIcon size={20} />,
        });

        peep.sendToDestinations.forEach((destination) => {
            menuOptions.push({
                label: <Link href={destination.localUrl}>{destination.name}</Link>,
                key: `${optionKey++}`,
                // The icon for custom send-tos is just an empty div. This spaces the
                // items corectly, lining them up with the other items, but we don't
                // have an actual logo to show.
                icon: <div className={styles.logo} />,
            });
        });

        return (
            <React.Fragment>
                {this.props.isOpen ? (
                    <SelectMenu
                        // @ts-expect-error
                        options={menuOptions}
                        anchor={this.props.anchor}
                        location='bottom-right'
                        onClose={this.props.onClose}
                    />
                ) : undefined}
                <div
                    ref={(node) => {
                        this.mailchimpPopoverDivRef = node;
                    }}
                    className='popover-opener-mailchimp popover-opener-ajax'
                    data-url='/send-to/mailchimp-popover.json'
                    data-target-selector='.peep-subheader .type-name'
                />
            </React.Fragment>
        );
    }

    handleSendToMailchimp = () => {
        if (this.mailchimpPopoverDivRef) {
            this.mailchimpPopoverDivRef.click();
        }
    };
}
